import React from "react";
import { Link } from "react-router-dom";
import { Copy, Star } from "lucide-react";
import { toast } from "sonner";
import { vehiclePrimaryImage, vehicleUsesPlaceholder } from "@/utils/vehicleImages";
import { formatVehiclePrice } from "@/utils/vehiclePrice";
import { vehicleStatusClass, vehicleStatusLabel } from "@/utils/vehicleStatus";
import AdminOverflowMenu from "@/components/admin/AdminOverflowMenu";

function copyVin(vin) {
  if (!vin) return;
  navigator.clipboard?.writeText(vin).then(
    () => toast.success("VIN copied"),
    () => toast.error("Could not copy VIN"),
  );
}

export default function AdminVehicleCard({ vehicle, overflowItems }) {
  const title = [vehicle.year, vehicle.make, vehicle.model].filter(Boolean).join(" ");
  const src = vehiclePrimaryImage(vehicle);
  const showPlaceholder = !src || vehicleUsesPlaceholder(vehicle);
  const price = formatVehiclePrice(vehicle);
  const vin = vehicle.vin || "";
  const vinShort = vin.length > 11 ? `${vin.slice(0, 8)}…${vin.slice(-4)}` : vin;

  return (
    <article
      className="group bg-white/5 border border-white/10 flex flex-col overflow-hidden hover:border-white/25 hover:bg-white/[0.07] transition-colors"
      data-testid={`inventory-card-${vehicle.id}`}
    >
      <Link
        to={`/admin/vehicles/${vehicle.id}`}
        className="relative block aspect-video bg-[#0a0a0a] overflow-hidden focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
      >
        {showPlaceholder ? (
          <div className="absolute inset-0 flex items-center justify-center px-4 text-center">
            <p className="font-['Unbounded'] text-sm text-white/40">{title || "No photo"}</p>
          </div>
        ) : (
          <img
            src={src}
            alt={title}
            loading="lazy"
            className="w-full h-full object-cover"
          />
        )}
        <span
          className={`absolute top-2 left-2 px-2 py-1 text-[10px] font-['Manrope'] font-semibold border ${vehicleStatusClass(vehicle.status)}`}
        >
          {vehicleStatusLabel(vehicle.status)}
        </span>
        {vehicle.featured && (
          <span className="absolute top-2 right-2 p-1 bg-black/50">
            <Star className="w-4 h-4 fill-yellow-400 text-yellow-400" />
          </span>
        )}
        <span className="absolute inset-x-0 bottom-0 py-2 text-center text-xs font-['Manrope'] font-semibold text-white bg-gradient-to-t from-black/80 to-transparent opacity-0 group-hover:opacity-100 transition-opacity">
          View details
        </span>
      </Link>

      <div className="p-4 flex flex-col gap-3 flex-1">
        <Link to={`/admin/vehicles/${vehicle.id}`} className="min-w-0 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500">
          <h3 className="font-['Manrope'] font-semibold text-white truncate">{title}</h3>
          <p className="text-xs font-['Manrope'] text-white/45 mt-0.5 truncate">
            {[vehicle.trim, vehicle.body_type].filter(Boolean).join(" · ") || "—"}
          </p>
        </Link>

        <p className="text-xl font-['Unbounded'] font-bold text-white">
          {price || <span className="text-amber-300 text-base">Price TBD</span>}
        </p>

        <dl className="space-y-1 text-sm font-['Manrope']">
          <div className="flex justify-between gap-2">
            <dt className="text-white/40">Stock</dt>
            <dd className="text-white/80 truncate">{vehicle.stockNumber || "—"}</dd>
          </div>
          <div className="flex justify-between gap-2 items-center">
            <dt className="text-white/40">VIN</dt>
            <dd>
              {vin ? (
                <button
                  type="button"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    copyVin(vin);
                  }}
                  className="inline-flex items-center gap-1 text-white/80 hover:text-white min-h-11"
                  title="Copy VIN"
                >
                  <span className="font-mono text-xs">{vinShort}</span>
                  <Copy className="w-3.5 h-3.5 text-white/40" />
                </button>
              ) : (
                <span className="text-white/80">—</span>
              )}
            </dd>
          </div>
          <div className="flex justify-between gap-2">
            <dt className="text-white/40">Mileage</dt>
            <dd className="text-white/80">
              {vehicle.mileage != null ? `${Number(vehicle.mileage).toLocaleString()} mi` : "—"}
            </dd>
          </div>
        </dl>

        <div className="mt-auto pt-3 border-t border-white/10 flex items-center gap-2">
          <Link
            to={`/admin/vehicles/${vehicle.id}`}
            className="flex-1 min-h-11 inline-flex items-center justify-center bg-blue-500 hover:bg-blue-600 text-white text-sm font-['Manrope'] font-semibold"
          >
            View details
          </Link>
          {overflowItems?.length > 0 && (
            <div
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
              }}
            >
              <AdminOverflowMenu items={overflowItems} />
            </div>
          )}
        </div>
      </div>
    </article>
  );
}
